import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Tab,
  Alert,
  CircularProgress,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import Tabs from '../UIKit/Tabs';
import { useTranslation } from 'react-i18next';

const BadgeAnalytics = ({
  analyticsTab,
  handleAnalyticsTabChange,
  popularityStats,
  difficultyStats,
  loadingAnalytics,
  analyticsError,
  getRarityColor,
}) => {
  const { t } = useTranslation(['badges']);
  // Компонент для отображения статистики популярности
  const PopularityAnalytics = () => {
    if (loadingAnalytics) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (analyticsError) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {analyticsError}
        </Alert>
      );
    }

    if (!popularityStats) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <Typography variant="body1" color="text.secondary">
            {t('badges:analytics.noData')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <PieChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('badges:analytics.popularity.title')}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('badges:analytics.popularity.earnedCount')}:{' '}
                  <strong>{popularityStats.totalBadges}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('badges:analytics.totalUsers')}:{' '}
                  <strong>{popularityStats.totalUsers}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('badges:analytics.avgPopularity')}:{' '}
                  <strong>
                    {popularityStats.badgeStats.length > 0
                      ? (
                          popularityStats.badgeStats.reduce(
                            (acc, badge) => acc + badge.popularityRate,
                            0
                          ) / popularityStats.badgeStats.length
                        ).toFixed(2)
                      : 0}
                    %
                  </strong>
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <TrendingUpIcon
                  sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }}
                />
                {t('badges:analytics.mostPopular')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('badges:analytics.badge')}</TableCell>
                      <TableCell>{t('badges:analytics.name')}</TableCell>
                      <TableCell>{t('badges:analytics.category')}</TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.owners')}
                      </TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.popularityRate')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {popularityStats.mostPopular.map((badge) => (
                      <TableRow key={badge._id}>
                        <TableCell>
                          <Avatar
                            src={badge.image}
                            alt={badge.name}
                            sx={{ width: 30, height: 30 }}
                          />
                        </TableCell>
                        <TableCell>{badge.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`badges:category.${badge.category}`)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{badge.usersCount}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${badge.popularityRate}%`}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <TrendingDownIcon
                  sx={{ verticalAlign: 'middle', mr: 1, color: 'error.main' }}
                />
                {t('badges:analytics.mostRare')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('badges:analytics.badge')}</TableCell>
                      <TableCell>{t('badges:analytics.name')}</TableCell>
                      <TableCell>{t('badges:analytics.rarity')}</TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.owners')}
                      </TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.popularityRate')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {popularityStats.mostRare.map((badge) => (
                      <TableRow key={badge._id}>
                        <TableCell>
                          <Avatar
                            src={badge.image}
                            alt={badge.name}
                            sx={{
                              width: 30,
                              height: 30,
                              border: `2px solid ${getRarityColor(
                                badge.rarity
                              )}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>{badge.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`badges:rarity.${badge.rarity}`)}
                            size="small"
                            sx={{
                              bgcolor: getRarityColor(badge.rarity),
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{badge.usersCount}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${badge.popularityRate}%`}
                            size="small"
                            color="error"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <BarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('badges:analytics.categoryStats')}
              </Typography>
              <List dense>
                {Object.entries(popularityStats.categoryStats).map(
                  ([category, stats]) => (
                    <ListItem key={category} divider>
                      <ListItemText
                        primary={t(`badges:category.${category}`)}
                        secondary={t('badges:analytics.badgesCount', {
                          count: stats.count,
                          users: stats.users,
                        })}
                      />
                    </ListItem>
                  )
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Компонент для отображения статистики сложности
  const DifficultyAnalytics = () => {
    if (loadingAnalytics) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (analyticsError) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {analyticsError}
        </Alert>
      );
    }

    if (!difficultyStats) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <Typography variant="body1" color="text.secondary">
            {t('badges:analytics.noData')}
          </Typography>
        </Box>
      );
    }

    // Функция для перевода категории сложности
    const translateDifficulty = (category) => {
      return t(`badges:analytics.difficultyLevel.${category}`);
    };

    // Функция для определения цвета сложности
    const getDifficultyColor = (category) => {
      const colors = {
        'very easy': '#4caf50',
        easy: '#8bc34a',
        medium: '#ffc107',
        hard: '#ff9800',
        'very hard': '#f44336',
      };
      return colors[category] || '#757575';
    };

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <AssessmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('badges:analytics.difficultyDistribution')}
              </Typography>
              <List dense>
                {Object.entries(difficultyStats.difficultyDistribution).map(
                  ([difficulty, count]) => (
                    <ListItem key={difficulty} divider>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: getDifficultyColor(difficulty),
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={translateDifficulty(difficulty)}
                        secondary={t('badges:analytics.difficultyCount', {
                          count,
                          percent: (
                            (count / difficultyStats.totalBadges) *
                            100
                          ).toFixed(1),
                        })}
                      />
                    </ListItem>
                  )
                )}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <TrendingUpIcon
                  sx={{ verticalAlign: 'middle', mr: 1, color: 'error.main' }}
                />
                {t('badges:analytics.hardest')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('badges:analytics.badge')}</TableCell>
                      <TableCell>{t('badges:analytics.name')}</TableCell>
                      <TableCell>{t('badges:analytics.rarity')}</TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.difficultyLabel')}
                      </TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.owners')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {difficultyStats.mostDifficult.map((badge) => (
                      <TableRow key={badge._id}>
                        <TableCell>
                          <Avatar
                            src={badge.image}
                            alt={badge.name}
                            sx={{
                              width: 30,
                              height: 30,
                              border: `2px solid ${getRarityColor(
                                badge.rarity
                              )}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>{badge.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`badges:rarity.${badge.rarity}`)}
                            size="small"
                            sx={{
                              bgcolor: getRarityColor(badge.rarity),
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={badge.difficultyScore + '/10'}
                            size="small"
                            sx={{
                              bgcolor: getDifficultyColor(
                                badge.difficultyCategory
                              ),
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {badge.usersWithBadge}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <TrendingDownIcon
                  sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }}
                />
                {t('badges:analytics.easiest')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('badges:analytics.badge')}</TableCell>
                      <TableCell>{t('badges:analytics.name')}</TableCell>
                      <TableCell>{t('badges:analytics.rarity')}</TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.difficultyLabel')}
                      </TableCell>
                      <TableCell align="right">
                        {t('badges:analytics.owners')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {difficultyStats.easiest.map((badge) => (
                      <TableRow key={badge._id}>
                        <TableCell>
                          <Avatar
                            src={badge.image}
                            alt={badge.name}
                            sx={{
                              width: 30,
                              height: 30,
                              border: `2px solid ${getRarityColor(
                                badge.rarity
                              )}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>{badge.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`badges:rarity.${badge.rarity}`)}
                            size="small"
                            sx={{
                              bgcolor: getRarityColor(badge.rarity),
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={badge.difficultyScore + '/10'}
                            size="small"
                            sx={{
                              bgcolor: getDifficultyColor(
                                badge.difficultyCategory
                              ),
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {badge.usersWithBadge}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 2, height: '100%', borderRadius: '16px' }}
            >
              <Typography variant="subtitle1" gutterBottom>
                <BarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('badges:analytics.generalInfo')}
              </Typography>
              <List dense>
                <ListItem divider>
                  <ListItemText
                    primary={t('badges:analytics.avgDifficulty')}
                    secondary={
                      difficultyStats.badgeDifficultyStats.length > 0
                        ? (
                            difficultyStats.badgeDifficultyStats.reduce(
                              (acc, badge) => acc + badge.difficultyScore,
                              0
                            ) / difficultyStats.badgeDifficultyStats.length
                          ).toFixed(1) + '/10'
                        : t('badges:analytics.notAvailable')
                    }
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText
                    primary={t('badges:analytics.totalAwarded')}
                    secondary={difficultyStats.badgeDifficultyStats.reduce(
                      (acc, badge) => acc + badge.usersWithBadge,
                      0
                    )}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Tabs
          value={analyticsTab}
          onChange={handleAnalyticsTabChange}
          aria-label="badge analytics tabs"
        >
          <Tab
            icon={<TrendingUpIcon />}
            label={t('badges:analytics.tabs.popularity')}
            id="badge-analytics-tab-0"
            aria-controls="badge-analytics-tabpanel-0"
          />
          <Tab
            icon={<AssessmentIcon />}
            label={t('badges:analytics.tabs.difficulty')}
            id="badge-analytics-tab-1"
            aria-controls="badge-analytics-tabpanel-1"
          />
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        hidden={analyticsTab !== 0}
        id="badge-analytics-tabpanel-0"
        aria-labelledby="badge-analytics-tab-0"
      >
        {analyticsTab === 0 && <PopularityAnalytics />}
      </Box>
      <Box
        role="tabpanel"
        hidden={analyticsTab !== 1}
        id="badge-analytics-tabpanel-1"
        aria-labelledby="badge-analytics-tab-1"
      >
        {analyticsTab === 1 && <DifficultyAnalytics />}
      </Box>
    </Box>
  );
};

export default BadgeAnalytics;
